import React, { useEffect, useState } from 'react';
import { ArrowGlyph, type ArrowIconName } from './GalleryCarousel';
import { parseVideoUrl } from './components/videoEmbed';

export interface VideoCarouselItem {
  url: string;
  startSeconds?: number;
}

/** Backward-compatible list: old video blocks stored `url` + `startSeconds`. */
export function videoItemsFromProps(props: any): VideoCarouselItem[] {
  if (Array.isArray(props?.videos) && props.videos.length > 0) {
    return props.videos
      .map((item: any) => typeof item === 'string' ? { url: item } : item)
      .filter((item: any) => item && typeof item.url === 'string');
  }
  return props?.url ? [{ url: props.url, startSeconds: props.startSeconds }] : [];
}

/**
 * One-video-at-a-time carousel shared by the builder preview and both public
 * renderers. Only the active iframe/video is mounted, so navigating stops the
 * previous video instead of leaving its audio playing in the background.
 */
export const VideoCarousel: React.FC<{
  videos: VideoCarouselItem[];
  radius?: string;
  arrowColor?: string;
  arrowBg?: string;
  arrowSize?: number;
  arrowRadius?: number;
  arrowShadow?: boolean;
  arrowIcon?: ArrowIconName;
  showDots?: boolean;
  autoPlay?: boolean;
  speed?: number;
  staticPreview?: boolean;
}> = ({
  videos,
  radius = '8px',
  arrowColor = '#111827',
  arrowBg = 'rgba(255,255,255,0.92)',
  arrowSize = 34,
  arrowRadius = 50,
  arrowShadow = true,
  arrowIcon = 'chevron',
  showDots = true,
  autoPlay = false,
  speed = 6,
  staticPreview = false,
}) => {
  const [index, setIndex] = useState(0);
  const [rtl, setRtl] = useState(false);
  const n = videos.length;
  const activeIndex = n ? ((index % n) + n) % n : 0;
  const active = videos[activeIndex];
  const embed = parseVideoUrl(active?.url, active?.startSeconds);

  useEffect(() => {
    setIndex(current => n ? Math.min(current, n - 1) : 0);
  }, [n]);

  useEffect(() => {
    if (staticPreview || !autoPlay || n < 2) return;
    const timer = window.setInterval(() => setIndex(current => (current + 1) % n), Math.max(2, speed || 6) * 1000);
    return () => window.clearInterval(timer);
  }, [autoPlay, n, speed, staticPreview]);

  useEffect(() => {
    if (typeof document !== 'undefined') setRtl(document.documentElement.dir === 'rtl');
  }, []);

  if (n === 0) return null;

  const size = Math.max(18, arrowSize || 34);
  const arrowStyle = (side: 'start' | 'end'): React.CSSProperties => ({
    position: 'absolute',
    [side === 'start' ? 'insetInlineStart' : 'insetInlineEnd']: 6,
    top: '50%',
    transform: 'translateY(-50%)',
    width: size,
    height: size,
    borderRadius: `${Math.max(0, Math.min(50, arrowRadius))}%`,
    border: 'none',
    background: arrowBg,
    color: arrowColor,
    cursor: staticPreview ? 'default' : 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: arrowShadow ? '0 2px 8px rgba(0,0,0,0.18)' : 'none',
    zIndex: 3,
    padding: 0,
  });

  const navigate = (delta: number) => {
    if (!staticPreview && n > 1) setIndex(current => (current + delta + n) % n);
  };

  return (
    <div style={{ position: 'relative', width: '100%', paddingBottom: '56.25%', borderRadius: radius, overflow: 'hidden', background: '#111827' }}>
      <div style={{ position: 'absolute', inset: 0 }}>
        {embed?.kind === 'iframe' ? (
          <iframe
            key={`${activeIndex}:${embed.src}`}
            src={embed.src}
            style={{ width: '100%', height: '100%', border: 'none' }}
            allowFullScreen
            title={`video ${activeIndex + 1}`}
          />
        ) : embed?.kind === 'video' ? (
          <video
            key={`${activeIndex}:${embed.src}`}
            src={embed.src}
            controls
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', textAlign: 'center', padding: 12 }}>
            {!active?.url ? 'הוסף קישור YouTube / Vimeo / MP4' : 'קישור הווידאו אינו תקין'}
          </div>
        )}
      </div>

      {n > 1 && (
        <>
          <button
            type="button"
            aria-label="הסרטון הקודם"
            style={arrowStyle('start')}
            onMouseDown={event => event.stopPropagation()}
            onClick={event => { event.stopPropagation(); event.preventDefault(); navigate(-1); }}
          >
            <ArrowGlyph icon={arrowIcon} flip={rtl} size={size} />
          </button>
          <button
            type="button"
            aria-label="הסרטון הבא"
            style={arrowStyle('end')}
            onMouseDown={event => event.stopPropagation()}
            onClick={event => { event.stopPropagation(); event.preventDefault(); navigate(1); }}
          >
            <ArrowGlyph icon={arrowIcon} flip={!rtl} size={size} />
          </button>
        </>
      )}

      {showDots && n > 1 && (
        <div style={{ position: 'absolute', insetInline: 0, bottom: 8, display: 'flex', justifyContent: 'center', gap: 5, zIndex: 3, pointerEvents: 'none' }}>
          {videos.map((_, dotIndex) => (
            <span key={dotIndex} style={{
              width: dotIndex === activeIndex ? 16 : 6,
              height: 6,
              borderRadius: 999,
              background: dotIndex === activeIndex ? arrowColor : 'rgba(255,255,255,0.85)',
              boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
              transition: 'width .2s',
            }} />
          ))}
        </div>
      )}
    </div>
  );
};
